/**
 * Gestione tenant — pagina admin unica con le sezioni del vecchio pannello Maeway:
 * Tenant · Ruoli · Associazioni R÷P (role-claims) · Permessi CRUD · Rotte ·
 * Ruoli utenti (user-roles) · Associazioni T÷U (user-tenants).
 *
 * Modello framework: i "permessi" sono claim `Entità.verbo` (es. Project.create),
 * le "rotte" sono le entità/risorse, le associazioni sono i join role-claims /
 * user-roles / user-tenants. Le tab con risorsa confermata sono operative; Rotte e
 * Permessi CRUD derivano dal catalogo claim/risorse (vedi nota in pagina).
 */
import { createFileRoute, Link } from '@tanstack/react-router';
import { Badge, Card, Group, Stack, Tabs, Text, TextInput, ThemeIcon, Button } from '@mantine/core';
import {
  IconBuilding, IconShieldCheck, IconKey, IconListCheck, IconRoute, IconUsers, IconUserShield, IconArrowRight, IconSearch,
} from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/shared/components/PageHeader';
import { ResourceListPage } from '@/shared/components/ResourceListPage';
import { useAuth } from '@/app/providers/AuthProvider';
import { fetchOne } from '@/shared/api/jsonapi/client';
import { formatDate } from '@/shared/lib/format';
import { STATIC_ROUTES } from '@/app/providers/SpotlightProvider';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

export const Route = createFileRoute('/_app/admin/tenant-management')({ component: TenantManagementPage });

interface Role extends Denormalized { type: 'roles'; name?: string; level?: number; typology?: string; roleType?: string; isFromMaster?: boolean }
interface RoleClaim extends Denormalized { type: 'role-claims'; claimType?: string; claimValue?: string; role?: { id: string; name?: string } | null }
interface UserRole extends Denormalized { type: 'user-roles'; user?: { id: string; email?: string } | null; role?: { id: string; name?: string } | null }
interface UserTenant extends Denormalized { type: 'user-tenants'; state?: string; ip?: string; acceptedAt?: string; user?: { id: string; email?: string } | null }
interface TenantRow extends Denormalized { type: 'tenants'; name?: string; businessName?: string; vatNumber?: string; enabled?: boolean }

function TenantManagementPage() {
  const { decoded } = useAuth();
  const tenantId = decoded?.tenantId ?? '';
  const tenantQ = useQuery({
    queryKey: ['tenant-mgmt-current', tenantId],
    enabled: Boolean(tenantId),
    queryFn: () => fetchOne<TenantRow>('tenants', tenantId),
  });
  const tenantName = tenantQ.data?.name ?? tenantQ.data?.businessName ?? (tenantId ? `Tenant ${tenantId.slice(0, 8)}` : '—');

  return (
    <Stack gap="lg">
      <PageHeader
        title="Gestione tenant"
        description={<Text span size="sm" c="dimmed">Ruoli, permessi, rotte e associazioni del tenant <b>{tenantName}</b>.</Text>}
      />

      <Tabs defaultValue="tenant" keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="tenant" leftSection={<IconBuilding size={16} />}>Tenant</Tabs.Tab>
          <Tabs.Tab value="roles" leftSection={<IconShieldCheck size={16} />}>Ruoli</Tabs.Tab>
          <Tabs.Tab value="role-perm" leftSection={<IconKey size={16} />}>Associazioni R÷P</Tabs.Tab>
          <Tabs.Tab value="crud-perm" leftSection={<IconListCheck size={16} />}>Permessi CRUD</Tabs.Tab>
          <Tabs.Tab value="routes" leftSection={<IconRoute size={16} />}>Rotte</Tabs.Tab>
          <Tabs.Tab value="user-roles" leftSection={<IconUserShield size={16} />}>Ruoli utenti</Tabs.Tab>
          <Tabs.Tab value="user-tenants" leftSection={<IconUsers size={16} />}>Associazioni T÷U</Tabs.Tab>
        </Tabs.List>

        {/* TENANT */}
        <Tabs.Panel value="tenant" pt="md">
          <Card withBorder radius="lg" padding="lg">
            <Group justify="space-between" wrap="nowrap">
              <Group gap="md" wrap="nowrap">
                <ThemeIcon variant="light" color="blue" size={48} radius="md"><IconBuilding size={26} /></ThemeIcon>
                <div>
                  <Group gap="xs">
                    <Text fw={700} fz="lg">{tenantName}</Text>
                    {tenantQ.data && (tenantQ.data.enabled ? <Badge color="green" variant="light">Attivo</Badge> : <Badge color="red" variant="light">Disattivato</Badge>)}
                  </Group>
                  <Text size="sm" c="dimmed">P.IVA {tenantQ.data?.vatNumber ?? '—'} · ID {tenantId.slice(0, 8)}</Text>
                </div>
              </Group>
              {tenantId && (
                <Button component={Link} to={`/admin/tenants/${tenantId}`} variant="light" rightSection={<IconArrowRight size={16} />}>
                  Apri scheda completa
                </Button>
              )}
            </Group>
          </Card>
        </Tabs.Panel>

        {/* RUOLI */}
        <Tabs.Panel value="roles" pt="md">
          <ResourceListPage<Role>
            title=""
            type="roles"
            columns={[
              { accessorKey: 'name', header: 'Ruolo' },
              { accessorKey: 'typology', header: 'Tipologia', size: 140 },
              { accessorKey: 'roleType', header: 'Tipo', size: 140 },
              { accessorKey: 'level', header: 'Livello', size: 100 },
              {
                accessorKey: 'isFromMaster', header: 'Origine', size: 130,
                Cell: ({ cell }) => cell.getValue<boolean>()
                  ? <Badge color="gray" variant="light">Sistema</Badge>
                  : <Badge color="blue" variant="light">Custom</Badge>,
              },
            ]}
            searchField="name"
            emptyMessage="Nessun ruolo"
            emptyIcon={IconShieldCheck}
          />
        </Tabs.Panel>

        {/* ASSOCIAZIONI R÷P (role-claims) */}
        <Tabs.Panel value="role-perm" pt="md">
          <ResourceListPage<RoleClaim>
            title=""
            type="role-claims"
            include={['role']}
            columns={[
              { accessorKey: 'role', header: 'Ruolo', Cell: ({ row }) => <Text size="sm">{row.original.role?.name ?? row.original.role?.id ?? '—'}</Text> },
              { accessorKey: 'claimType', header: 'Tipo claim', size: 160 },
              { accessorKey: 'claimValue', header: 'Permesso' },
            ]}
            searchField="claimValue"
            emptyMessage="Nessuna associazione ruolo↔permesso"
            emptyIcon={IconKey}
          />
        </Tabs.Panel>

        {/* PERMESSI CRUD */}
        <Tabs.Panel value="crud-perm" pt="md">
          <ResourceListPage<RoleClaim>
            title=""
            type="role-claims"
            include={['role']}
            columns={[
              { accessorKey: 'claimValue', header: 'Permesso (Entità.verbo)' },
              { accessorKey: 'claimType', header: 'Tipo', size: 160 },
              { accessorKey: 'role', header: 'Ruolo', Cell: ({ row }) => <Text size="sm">{row.original.role?.name ?? '—'}</Text> },
            ]}
            searchField="claimValue"
            emptyMessage="Nessun permesso"
            emptyIcon={IconListCheck}
          />
          <Text size="xs" c="dimmed" mt="xs">
            I permessi CRUD sono claim nel formato <code>Entità.verbo</code> (es. <code>Project.create</code>). Il set completo dei permessi assegnabili deriva dal catalogo risorse del BE.
          </Text>
        </Tabs.Panel>

        {/* ROTTE (= pagine dell'applicativo) */}
        <Tabs.Panel value="routes" pt="md">
          <RoutesCatalog />
        </Tabs.Panel>

        {/* RUOLI UTENTI (user-roles) */}
        <Tabs.Panel value="user-roles" pt="md">
          <ResourceListPage<UserRole>
            title=""
            type="user-roles"
            include={['user', 'role']}
            columns={[
              { accessorKey: 'user', header: 'Utente', Cell: ({ row }) => <Text size="sm">{row.original.user?.email ?? row.original.user?.id ?? '—'}</Text> },
              { accessorKey: 'role', header: 'Ruolo', Cell: ({ row }) => <Text size="sm">{row.original.role?.name ?? row.original.role?.id ?? '—'}</Text> },
            ]}
            emptyMessage="Nessun ruolo assegnato agli utenti"
            emptyIcon={IconUserShield}
          />
        </Tabs.Panel>

        {/* ASSOCIAZIONI T÷U (user-tenants) */}
        <Tabs.Panel value="user-tenants" pt="md">
          <ResourceListPage<UserTenant>
            title=""
            type="user-tenants"
            include={['user']}
            columns={[
              { accessorKey: 'user', header: 'Utente', Cell: ({ row }) => <Text size="sm">{row.original.user?.email ?? row.original.user?.id ?? '—'}</Text> },
              {
                accessorKey: 'state', header: 'Stato', size: 140,
                Cell: ({ cell }) => { const s = cell.getValue<string>(); return s ? <Badge variant="light">{s}</Badge> : '—'; },
              },
              { accessorKey: 'acceptedAt', header: 'Accettato il', size: 140, Cell: ({ cell }) => <Text size="sm">{formatDate(cell.getValue<string>())}</Text> },
              { accessorKey: 'ip', header: 'IP', size: 140 },
            ]}
            emptyMessage="Nessuna associazione utente↔tenant"
            emptyIcon={IconUsers}
          />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

/** Catalogo delle pagine/rotte dell'applicativo, raggruppate per sezione, con ricerca. */
function RoutesCatalog() {
  const [q, setQ] = useState('');
  const sections = useMemo(() => {
    const query = q.trim().toLowerCase();
    const match = (r: (typeof STATIC_ROUTES)[number]) =>
      !query
      || r.label.toLowerCase().includes(query)
      || r.to.toLowerCase().includes(query)
      || (r.keywords ?? []).some((k) => k.toLowerCase().includes(query));
    const order: string[] = [];
    const map = new Map<string, Array<(typeof STATIC_ROUTES)[number]>>();
    for (const r of STATIC_ROUTES) {
      if (!match(r)) continue;
      if (!map.has(r.section)) { map.set(r.section, []); order.push(r.section); }
      map.get(r.section)!.push(r);
    }
    return order.map((s) => ({ section: s, rows: map.get(s)! }));
  }, [q]);
  const total = sections.reduce((s, g) => s + g.rows.length, 0);

  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="wrap" gap="sm">
        <TextInput placeholder="Cerca pagina…" leftSection={<IconSearch size={16} />} value={q} onChange={(e) => setQ(e.currentTarget.value)} w={320} />
        <Text size="sm" c="dimmed">{total} pagine</Text>
      </Group>
      <Text size="xs" c="dimmed">
        Catalogo delle pagine dell'applicativo. L'abilitazione per singolo tenant verrà collegata quando il BE espone il campo dedicato.
      </Text>
      {sections.map((g) => (
        <Card key={g.section} withBorder radius="lg" padding="md">
          <Group justify="space-between" mb="sm">
            <Text fw={700} tt="uppercase" size="sm" c="dimmed">{g.section}</Text>
            <Badge variant="light">{g.rows.length}</Badge>
          </Group>
          <Stack gap={6}>
            {g.rows.map((r) => (
              <Group key={r.to} justify="space-between" wrap="nowrap" p="xs" style={{ borderRadius: 8, border: '1px solid var(--mantine-color-default-border)' }}>
                <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                  <ThemeIcon variant="light" size={32} radius="md"><r.icon size={18} /></ThemeIcon>
                  <Text size="sm" fw={500} lineClamp={1}>{r.label}</Text>
                </Group>
                <Text size="xs" c="dimmed" style={{ fontFamily: 'var(--mantine-font-family-monospace)', whiteSpace: 'nowrap' }}>{r.to}</Text>
              </Group>
            ))}
          </Stack>
        </Card>
      ))}
      {total === 0 && <Text c="dimmed" ta="center" py="md">Nessuna pagina trovata.</Text>}
    </Stack>
  );
}
